import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { listLeadSearchItems } from "../lib/api";
import { angleLabel, shortDate, skipReasonLabel, usd } from "../lib/leads";
import type { LeadSearchItems, LeadSearchRow, QueueItem } from "../lib/types";
import { formatApiError } from "../lib/utils";
import { Icon } from "./Icon";
import { Button, Select } from "./ui";

type Group = {
  key: string;
  title: string;
  kind: string;
  angle: string;
  project: string;
  vendor: string;
  rows: LeadSearchRow[];
};

function rowKey(row: LeadSearchRow): string {
  return String(row.item.id || `${row.objective_id}-${row.item.query}`);
}

/** Ticked by default: only queries that never ran and that the next run will execute. */
function defaultSelection(rows: LeadSearchRow[]): Record<string, boolean> {
  const initial: Record<string, boolean> = {};
  for (const row of rows) initial[rowKey(row)] = row.will_run && row.run_state === "first_run";
  return initial;
}

function kindLabel(kind: string): string {
  if (kind === "roadmap_evidence") return "Roadmap: find support";
  if (kind === "roadmap_status_refresh") return "Roadmap: status update";
  return "Lead";
}

/**
 * The lead and roadmap research planned for the Search page's bank, one row per
 * query, each marked first run or ran before.  Selected rows are sent with
 * "Run searches" as ordinary research-labelled queue items.
 */
export function LeadResearchPanel({
  target,
  refreshKey = 0,
  disabled = false,
  onSelectionChange,
  onSwitchTarget,
}: {
  target: string;
  refreshKey?: number;
  disabled?: boolean;
  onSelectionChange: (items: QueueItem[]) => void;
  onSwitchTarget: (targetName: string) => void;
}) {
  const [data, setData] = useState<LeadSearchItems | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState(true);
  // "" = every vendor cluster; otherwise only that cluster's rows are shown and run.
  const [vendorFilter, setVendorFilter] = useState("");
  // Every request carries a number; an answer for anything but the newest one
  // is dropped, so a slow reply for the previous bank can never land here.
  const requestRef = useRef(0);
  const scope = target.trim();
  const debouncedScope = useDebouncedValue(scope, 400);

  // The moment the bank changes, the old bank's rows and ticks are gone: they
  // must never ride along with a run for another bank.
  useEffect(() => {
    requestRef.current += 1;
    setData(null);
    setSelected({});
    setError("");
    setVendorFilter("");
  }, [scope]);

  const load = useCallback(async (bank: string) => {
    const request = ++requestRef.current;
    if (!bank) {
      setData(null);
      setSelected({});
      return;
    }
    setLoading(true);
    setError("");
    try {
      const next = await listLeadSearchItems(bank);
      if (request !== requestRef.current) return;
      setData(next);
      setSelected(defaultSelection(next.items));
    } catch (e) {
      if (request !== requestRef.current) return;
      setError(formatApiError(e));
      setData(null);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(debouncedScope);
  }, [load, debouncedScope, refreshKey]);

  const allRows = useMemo(() => data?.items || [], [data]);
  const vendors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of allRows) {
      const vendor = String(row.vendor || "").trim();
      if (vendor) counts.set(vendor, (counts.get(vendor) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [allRows]);
  const rows = useMemo(
    () => (vendorFilter ? allRows.filter((row) => String(row.vendor || "").trim() === vendorFilter) : allRows),
    [allRows, vendorFilter],
  );

  // Only rows the vendor filter shows are sent: a hidden tick never runs.
  useEffect(() => {
    onSelectionChange(
      rows
        .filter((row) => selected[rowKey(row)])
        .map((row) => ({ ...row.item, selected: true }) as QueueItem),
    );
  }, [rows, selected, onSelectionChange]);

  const groups = useMemo(() => {
    const map = new Map<string, Group>();
    for (const row of rows) {
      const key = row.objective_id;
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: row.lead,
          kind: row.objective_kind,
          angle: row.angle,
          project: row.project_name,
          vendor: String(row.vendor || ""),
          rows: [],
        });
      }
      map.get(key)!.rows.push(row);
    }
    return [...map.values()];
  }, [rows]);

  const selectedCount = rows.filter((row) => selected[rowKey(row)]).length;
  const selectedLeads = new Set(rows.filter((row) => selected[rowKey(row)]).map((row) => row.objective_id)).size;
  const budget = data?.budget;
  const fundable = budget?.fundable_leads;
  const firstRuns = rows.filter((row) => row.run_state === "first_run").length;
  const skipped = rows.filter((row) => !row.will_run).length;
  const others = (data?.other_targets || []).filter(
    (t) => t.target_name.toLowerCase() !== (data?.target_name || target).trim().toLowerCase(),
  );

  const setMany = (predicate: (row: LeadSearchRow) => boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const row of rows) next[rowKey(row)] = predicate(row);
      return next;
    });
  };

  if (!target.trim()) return null;

  return (
    <section className="border border-tertiary/30 bg-tertiary/5 shrink-0">
      <div className="px-4 py-2 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex items-center gap-2 font-data-label text-data-label uppercase text-tertiary"
          aria-expanded={open}
        >
          <Icon name={open ? "expand_less" : "expand_more"} />
          Lead research
        </button>
        <span className="text-body-sm font-data-mono text-on-surface-variant">
          {loading
            ? "Loading…"
            : `${rows.length} ${rows.length === 1 ? "query" : "queries"} · ${firstRuns} first run · ${skipped} will be skipped · ${selectedCount} selected`}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {vendors.length > 1 ? (
            <Select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="py-1 w-auto max-w-[16rem]"
              aria-label="Show lead research for one vendor cluster"
            >
              <option value="">All vendor clusters ({allRows.length})</option>
              {vendors.map(([vendor, count]) => (
                <option key={vendor} value={vendor}>
                  {vendor} ({count})
                </option>
              ))}
            </Select>
          ) : null}
          <Button variant="link-muted" disabled={disabled || !rows.length} onClick={() => setMany((row) => row.will_run)}>
            Select runnable
          </Button>
          <Button
            variant="link-muted"
            disabled={disabled || !rows.length}
            onClick={() => setMany((row) => row.will_run && row.run_state === "first_run")}
          >
            First runs only
          </Button>
          <Button variant="link-muted" disabled={disabled || !selectedCount} onClick={() => setMany(() => false)}>
            Clear
          </Button>
          <Button variant="link-muted" disabled={loading} onClick={() => void load(scope)}>
            Reload
          </Button>
        </div>
      </div>

      {open ? (
        <div className="px-4 pb-3 space-y-3">
          {error ? <p className="text-body-sm text-error">{error}</p> : null}
          {!loading && !error && !rows.length ? (
            <p className="text-body-sm text-on-surface-variant">
              No planned lead research for {data?.target_name || target}. On Projects, select projects and use
              &nbsp;&ldquo;Send leads to Search&rdquo;.
            </p>
          ) : null}
          {others.length ? (
            <div className="flex flex-wrap items-center gap-2 text-body-sm">
              <span className="text-on-surface-variant">Other banks with planned lead research:</span>
              {others.map((t) => (
                <Button
                  key={t.target_name}
                  variant="outline-primary"
                  size="sm"
                  disabled={disabled}
                  onClick={() => onSwitchTarget(t.target_name)}
                >
                  {t.target_name} · {t.queries}
                </Button>
              ))}
            </div>
          ) : null}
          {selectedCount ? (
            <p className="text-[11px] text-on-surface-variant">
              The {selectedCount} ticked {selectedCount === 1 ? "query runs" : "queries run"} with the next Run searches for{" "}
              {data?.target_name || scope}; automatic research selection is skipped for that run.
            </p>
          ) : null}
          {budget && budget.per_lead_usd != null ? (
            <p className={`text-[11px] ${fundable != null && selectedLeads > fundable ? "text-error" : "text-on-surface-variant"}`}>
              Each lead&apos;s research reserves about {usd(budget.per_lead_usd)} end to end (search, pages, extraction,
              reconciliation).
              {fundable != null
                ? ` This bank's next run can fund about ${fundable} lead${fundable === 1 ? "" : "s"} (${usd(budget.pool_usd)} research pool).`
                : " This bank has no spend ledger yet."}
              {fundable != null && selectedLeads > fundable
                ? ` You selected ${selectedLeads}; the rest stay queued for the next run.`
                : ""}
            </p>
          ) : null}
          {data && !data.search_v3 && rows.some((row) => row.skip_reason === "already_ran_for_target") ? (
            <p className="text-[11px] text-on-surface-variant">
              &ldquo;Ran before&rdquo; means the query already produced evidence for this bank; with ORION1_SEARCH_V3 off,
              Phase 1 does not repeat it.
            </p>
          ) : null}
          <div className="space-y-2 max-h-[42vh] overflow-y-auto custom-scrollbar pr-1">
            {groups.map((group) => (
              <div key={group.key} className="border border-outline-variant bg-surface-container px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-body-sm font-semibold text-on-surface">{group.title}</span>
                  <span className="text-[10px] font-data-mono uppercase text-tertiary">{kindLabel(group.kind)}</span>
                  {group.angle ? (
                    <span className="text-[10px] font-data-mono uppercase text-outline">{angleLabel(group.angle)}</span>
                  ) : null}
                  {group.project ? (
                    <span className="text-[10px] font-data-mono text-on-surface-variant truncate">{group.project}</span>
                  ) : null}
                  {group.vendor ? (
                    <span className="text-[10px] font-data-mono uppercase text-primary truncate">{group.vendor}</span>
                  ) : null}
                </div>
                <ul className="mt-1 space-y-1">
                  {group.rows.map((row) => {
                    const key = rowKey(row);
                    const reason = skipReasonLabel(row);
                    return (
                      <li key={key} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={Boolean(selected[key])}
                          disabled={disabled}
                          onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
                          aria-label={`Run ${row.item.query}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-data-mono text-body-sm text-on-surface break-words">{row.item.query}</p>
                          <div className="flex flex-wrap items-center gap-2 text-[10px] font-data-mono">
                            {row.run_state === "first_run" ? (
                              <span className="px-1.5 py-0.5 border border-secondary/40 text-secondary bg-secondary/10 rounded uppercase">
                                First run
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 border border-outline-variant text-on-surface-variant rounded uppercase">
                                Ran before
                                {row.run_count ? ` · ${row.run_count}×` : ""}
                                {row.last_run_at ? ` · ${shortDate(row.last_run_at)}` : ""}
                              </span>
                            )}
                            <span className="text-outline">
                              research attempt {row.attempts}/{row.max_attempts}
                            </span>
                            {reason ? <span className="text-tertiary">{reason}</span> : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
